import { useEffect, useState, useCallback, useRef } from 'react'
import {
  CASE_GROUPS, CASE_META, ACQ_FIELDS, PART_FIELDS, PART_TABLE_COLS, fmtValue, statusClasses,
} from './trackerFields'
import { getTrackerCase } from '../services/api'

// READ-ONLY drawer for one CASE. The Google Sheet is the source of truth —
// corrections are made in the sheet and appear here after the next sync.

export default function TrackerCaseDrawer({ caseId, onClose }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try { const r = await getTrackerCase(caseId); setData(r.data) }
    catch (e) { setError(e.response?.data?.error || 'Failed to load case') }
    finally { setLoading(false) }
  }, [caseId])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  if (loading) {
    return <Shell onClose={onClose}><div className="p-6 text-gray-400">Loading…</div></Shell>
  }
  const c = data || {}

  return (
    <Shell onClose={onClose} wide>
      {/* header */}
      <div className="px-5 py-4 border-b border-gray-200">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-base font-semibold text-gray-900 font-mono truncate">{c.caseNo}</div>
            {/* referral, not patient — the Tracker shows no patient data */}
            <div className="text-sm text-gray-500 truncate">{[c.doctorName, c.hospitalName].filter(Boolean).join(' · ') || 'No referral details'}</div>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700 text-xl px-1">✕</button>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusClasses(c.status)}`}>{c.status}</span>
          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{c.partCount ?? c.parts?.length ?? 0} parts</span>
          {c.year != null && <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">{c.year}</span>}
        </div>
        <p className="text-[11px] text-gray-400 mt-2">Read-only — edit in the Tracker sheet; changes appear after the next sync.</p>
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </div>

      {/* body */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
        {CASE_GROUPS.map(g => {
          const rows = g.fields.filter(([k, , d]) => fmtValue(k, c[k], d) !== '')
          if (rows.length === 0) return null
          return (
            <section key={g.title}>
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">{g.title}</h4>
              <dl className="grid grid-cols-1 gap-y-1.5">
                {rows.map(([k, label, d]) => (
                  <div key={k} className="flex gap-3 text-sm items-center">
                    <dt className="w-36 shrink-0 text-gray-400">{label}</dt>
                    <dd className="flex-1 min-w-0">{fmtValue(k, c[k], d)}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )
        })}

        {/* DICOM acquisitions — one card per distinct tuple (a case whose
            data was segmented multiple times, e.g. skull + leg, has >1) */}
        {(c.acquisitions?.length > 0) && (
          <section>
            <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
              DICOM Acquisition{c.acquisitions.length > 1 ? `s (${c.acquisitions.length})` : ''}
            </h4>
            <div className="space-y-3">
              {c.acquisitions.map((a, i) => (
                <div key={i} className="border border-gray-200 rounded-lg px-3 py-2">
                  {c.acquisitions.length > 1 && (
                    <div className="text-[11px] text-gray-400 mb-1.5">
                      Acquisition {i + 1}{a.partLabels?.length ? ` — parts ${a.partLabels.join(', ')}` : ''}
                    </div>
                  )}
                  <dl className="grid grid-cols-1 gap-y-1">
                    {ACQ_FIELDS.filter(([k, , d]) => fmtValue(k, a[k], d) !== '').map(([k, label, d]) => (
                      <div key={k} className="flex gap-3 text-sm items-center">
                        <dt className="w-36 shrink-0 text-gray-400">{label}</dt>
                        <dd className="flex-1 min-w-0">{fmtValue(k, a[k], d)}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              ))}
            </div>
          </section>
        )}

        <section>
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Identity</h4>
          <dl className="grid grid-cols-2 gap-y-1.5">
            {CASE_META.map(([k, label]) => c[k] != null && c[k] !== '' && (
              <div key={k} className="text-xs"><span className="text-gray-400">{label}: </span><span className="text-gray-700">{String(c[k])}</span></div>
            ))}
          </dl>
        </section>

        <PartsTable parts={c.parts || []} />
      </div>
    </Shell>
  )
}

function Shell({ children, onClose, wide }) {
  const panelRef = useRef(null)
  useEffect(() => { panelRef.current?.focus() }, [])
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-gray-900/30" onClick={onClose} />
      <div className={`relative w-full ${wide ? 'max-w-[720px]' : 'max-w-[460px]'} bg-white h-full shadow-xl flex flex-col`} tabIndex={-1} ref={panelRef}>{children}</div>
    </div>
  )
}

function PartsTable({ parts }) {
  const [openId, setOpenId] = useState(null)

  return (
    <section>
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Parts / STL files ({parts.length})</h4>
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="bg-gray-50 text-left text-gray-500">
            {PART_TABLE_COLS.map(k => <th key={k} className="px-2 py-1.5 font-medium whitespace-nowrap">{PART_FIELDS.find(f => f[0] === k)?.[1] || k}</th>)}
            <th />
          </tr></thead>
          <tbody className="divide-y divide-gray-100">
            {parts.length === 0 && <tr><td colSpan={PART_TABLE_COLS.length + 1} className="px-2 py-3 text-center text-gray-400">No parts.</td></tr>}
            {parts.map(p => (
              <PartRow key={p.id} part={p} open={openId === p.id} onToggle={() => setOpenId(openId === p.id ? null : p.id)} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function PartRow({ part: p, open, onToggle }) {
  return (
    <>
      <tr className="hover:bg-gray-50 cursor-pointer" onClick={onToggle}>
        {PART_TABLE_COLS.map(k => <td key={k} className="px-2 py-1.5 max-w-[160px] truncate" title={fmtValue(k, p[k], /date|dispatch/i.test(k))}>{k === 'kind'
          ? <span className={`px-1.5 py-0.5 rounded text-[10px] ${p.kind === 'REFERENCE' ? 'bg-gray-100 text-gray-500' : 'bg-blue-50 text-blue-600'}`}>{p.kind}</span>
          : fmtValue(k, p[k], /date|dispatch|cleaned|packed/i.test(k))}</td>)}
        <td className="px-2 text-gray-300 text-right">{open ? '▾' : '▸'}</td>
      </tr>
      {open && (
        <tr className="bg-gray-50/60">
          <td colSpan={PART_TABLE_COLS.length + 1} className="px-3 py-2">
            <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
              {PART_FIELDS.filter(([k, , d]) => fmtValue(k, p[k], d) !== '').map(([k, label, d]) => (
                <div key={k} className="text-[11px]">
                  <span className="text-gray-400">{label}: </span>
                  <span className="text-gray-700">{fmtValue(k, p[k], d)}</span>
                </div>
              ))}
            </dl>
          </td>
        </tr>
      )}
    </>
  )
}
